import React, {useEffect, useMemo, useRef, useState} from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import {
	Alert,
	Button,
	EMPHASIS,
	FlexColumn,
	FlexRow,
	Heading,
	Label,
	Separator,
	Text,
	Tooltip,
} from '@forecast-it/design-system';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../shared/components/headers/top-header-bar/TopHeaderBar';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import Styled from 'styled-components';
import Util from '../../shared/util/util';
import {createToast} from '../../shared/components/toasts/toast';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';
import {useTrackPage} from '../../../tracking/amplitude/hooks/useTrackPage';
import {StageBandBar} from '../../shared/components/opportunities/StageBandBar';
import UpdateOpportunityStageBandsMutation from '../../../mutations/update_opportunity_stage_bands_mutation.modern';
import ResetOpportunityStageBandsMutation from '../../../mutations/reset_opportunity_stage_bands_mutation.modern';
import {showResetOpportunityStagesModal} from './modals/ResetOpportunityStagesModal';
import {EditableOpportunityStageRow, LockedOpportunityStageRow} from './OpportunityStageRow';
import type {SettingsOpportunityStages_viewer$key} from './__generated__/SettingsOpportunityStages_viewer.graphql';
import {
	MAX_BANDS,
	addBand,
	addBandBlock,
	canAddBand,
	canDeleteBand,
	deleteBand,
	formatWinProbability,
	parseServerRejection,
	setBandEnd,
	setBandWinProbability,
	validateBands,
	withStarts,
	type BandError,
	type ServerRejection,
	type StageBand,
} from './opportunityStageBands';

// Spacing mirrors the Currencies settings page so the two read as the same kind of admin screen.
const Page = Styled.div`
	padding: 0 24px;
`;
const AlertContainer = Styled.div`
	margin: 16px 0;
`;
const HeadingContainer = Styled.div`
	margin: 12px 0;
`;
// Sits directly under the page title, so the two read as one heading block.
const Subtitle = Styled.div`
	padding: 8px 0 4px;
`;
// The banner's second paragraph. Block-level so it sits under the first rather than beside it, and a span
// because it renders inside the alert's own text element.
const BannerParagraph = Styled.span`
	display: block;
	&:not(:first-child) {
		padding-top: 8px;
	}
`;
const PreviewBar = Styled.div`
	padding-bottom: 24px;
`;
const Actions = Styled.div`
	padding-top: 16px;
	/* The design system button spaces its icon from its label generously; these two are text-style
	   buttons where the icon reads as part of the label, so the gap is tightened. */
	& button {
		gap: 4px;
	}
`;
const HeaderRow = Styled.div`
	display: grid;
	/* Kept in step with the row grid in OpportunityStageRow, so headers stay over their columns. */
	grid-template-columns: minmax(0, 1fr) 120px minmax(0, 1fr) 32px;
	/* Kept in step with the row's gap, columns and edge offsets, or the headings drift off their fields. */
	gap: 16px 32px;
	padding-bottom: 8px;
	padding-left: 8px;
	padding-right: 16px;
	/* The range heading carries the same leading offset as the range cell below it. */
	& > *:nth-child(3) {
		padding-left: 24px;
	}
`;

const FIXED_LOST_PERCENTAGE = 0;
const FIXED_WON_PERCENTAGE = 100;

type Props = {
	viewer: SettingsOpportunityStages_viewer$key;
};

// The alert renders its `text` as the children of a Text component, so rich content works at runtime
// even though the prop is typed as a string. Passing it here keeps the banner inside the design system
// component; the alternative is rebuilding the alert's fill, icon, bar and dismiss button by hand.
// (Its `children` slot is not an option — that renders to the right of the text, not below it.)
const asAlertText = (content: React.ReactNode): string => content as unknown as string;

/** A comparable fingerprint of a band set, trimmed so trailing whitespace never counts as a change. */
const bandsKey = (list: StageBand[]): string =>
	JSON.stringify(list.map(band => ({name: band.name.trim(), end: band.end, winProbability: band.winProbability})));

/** What a bound field shows: the text as typed while it is being edited, else the stored value. */
const textFor = (drafts: Record<number, string>, index: number, value: number): string => {
	const draft = drafts[index];
	if (draft !== undefined) {
		return draft;
	}
	return Number.isNaN(value) ? '' : String(value);
};

const errorFor = (errors: BandError[], index: number, field: BandError['field']): BandError | undefined =>
	errors.find(error => error.index === index && error.field === field);

/** The translated inline error for one field of one row, or undefined when that field is valid. */
const errorTextFor = (
	errors: BandError[],
	index: number,
	field: BandError['field'],
	formatMessage: (descriptor: {id: string}) => string
): string | undefined => {
	const error = errorFor(errors, index, field);
	return error ? formatMessage({id: error.messageId}) : undefined;
};

export const SettingsOpportunityStages = ({viewer}: Props): JSX.Element => {
	const {formatMessage} = useIntl();

	// Every tracked event carries the page it came from; without this the page reports none and each
	// event logs an error instead.
	useTrackPage('Opportunity Stages');

	const data = useFragment(
		graphql`
			fragment SettingsOpportunityStages_viewer on Viewer {
				company {
					id
					opportunityStageBands(first: 1000) {
						edges {
							node {
								id
								name
								startPercentage
								endPercentage
								winProbability
							}
						}
					}
				}
			}
		`,
		viewer
	);

	const serverBands: StageBand[] = useMemo(
		() =>
			(data.company?.opportunityStageBands?.edges ?? []).flatMap(edge =>
				edge?.node
					? [
							{
								id: edge.node.id,
								name: edge.node.name ?? '',
								end: edge.node.endPercentage ?? 0,
								winProbability: edge.node.winProbability ?? 0,
							},
					  ]
					: []
			),
		[data]
	);

	// Local copy so edits and the preview bar update instantly; the server's response replaces it.
	const [bands, setBands] = useState<StageBand[]>(serverBands);
	const [errors, setErrors] = useState<BandError[]>([]);
	// Raw text per row index, held only while a row's end field is being edited.
	const [endDrafts, setEndDrafts] = useState<Record<number, string>>({});
	// The same, for the win probability. Both are kept so a part-deleted value shows an empty box rather
	// than "NaN", while the parsed value stays NaN and blocks the commit.
	const [winProbabilityDrafts, setWinProbabilityDrafts] = useState<Record<number, string>>({});
	const [infoDismissed, setInfoDismissed] = useState(false);
	// The row that should take the cursor, set when one is appended and released as soon as it has. Held
	// for a single render rather than kept: it says "give this row focus now", not "this row is focused",
	// so leaving it set would drag the cursor back on every later render.
	const [focusIndex, setFocusIndex] = useState<number | null>(null);
	// A rejection the server explained. Held on the page rather than shown in a modal: the generic
	// mutation modal has no field for a reason, so routing it there would discard the very text the
	// server went to the trouble of stating.
	const [rejection, setRejection] = useState<ServerRejection | null>(null);
	// The last set actually written to the server (or read from it). Blurring a field that was never
	// changed — clicking into a row and back out, tabbing through the table — must not repost the same
	// set: every blur on every field calls this, so without a change check it would fire and toast on
	// every click regardless of whether anything was edited.
	const [lastCommitted, setLastCommitted] = useState<StageBand[]>(serverBands);
	// The same value, readable from the mutation callbacks. Those close over the render that sent the write,
	// so by the time one runs the state it captured can be several edits old — the very staleness this whole
	// mechanism exists to avoid. State is kept alongside it because the resync effect below reads it as a
	// dependency, which a ref cannot provide.
	const lastCommittedRef = useRef<StageBand[]>(serverBands);
	const recordCommitted = (set: StageBand[]) => {
		lastCommittedRef.current = set;
		setLastCommitted(set);
	};
	// The set currently being written, held from the moment it is sent until the response is handled. A ref
	// rather than state: the store is updated before the success callback runs, so by the time the resync
	// effect sees the response, what was written is not yet recorded — and the effect reads a set one edit
	// old, takes the response for someone else's change, and adopts it over everything done since. Recording
	// it in state instead would re-render, and the effect would then compare the set being written against a
	// server set that has not caught up, discarding the very edit that is in flight.
	const inFlight = useRef<StageBand[] | null>(null);
	// The newest set still to be written, held while another write is out. Only ever one, because every
	// write posts the whole list: a later list already contains every edit an earlier one carried, so the
	// versions in between are duplicates rather than changes, and dropping them loses nothing. This is what
	// keeps a burst of blurs down to the write in flight plus one carrying everything since.
	const pending = useRef<StageBand[] | null>(null);
	// Bumped whenever something supersedes the writes already under way — currently only a reset. A write
	// sent before the bump must not report back, because by the time it answers its result describes a set
	// the page has deliberately moved on from: letting its success run would record the superseded set as
	// what the server holds, and the page would then adopt it back over the defaults reset had just applied.
	const writeGeneration = useRef(0);
	// A reset that has been confirmed but cannot be sent yet, because a write is still out. Superseding the
	// queue is not enough on its own: a write already at the server is beyond recall, and sending the reset
	// alongside it leaves the server to commit them in whichever order it finishes, so the write can land
	// second and put back the very set the reset was asked to discard. Waiting for the response is what
	// orders them — a response means that write is done, so the reset can only commit after it.
	const pendingReset = useRef(false);
	// Whether a reset is itself outstanding. It is not one of the band writes, so it is not in `inFlight`,
	// and without its own marker a blur landing while it is out would send an update alongside it — the very
	// race waiting for the write was meant to end, just with the two swapped over. Edits made in that window
	// are dropped rather than held: they describe the set the reset is discarding, so writing them next
	// would undo it, and the resync replaces what is on screen regardless.
	const resetInFlight = useRef(false);
	// Bumped by a reset to make the next resync unconditional. A reset has to be adopted even when the
	// server's set matches what was last written — an account already on the defaults — or a cleared field
	// and its error would sit there under a toast saying the change was saved. Said with its own signal
	// rather than by emptying what was last committed: that made the page briefly believe every row was
	// unsaved, so the status line contradicted the toast for as long as it took the resync to run.
	const [resyncRequests, setResyncRequests] = useState(0);
	const resyncsHandled = useRef(0);

	useEffect(() => {
		// useFragment yields a fresh object on any store write touching this selection — including the
		// response to a commit the user triggered a moment ago. Resyncing on identity alone would then
		// discard whatever they have typed in the meantime: blur one row to save it, start typing the next,
		// and the response landing would wipe the second row mid-edit. Only a set that genuinely differs
		// from what we last wrote is worth overwriting local state for — another admin's change, or the
		// server normalising our own payload.
		// A reset asks for the server's set whatever is on screen, so it overrides both checks below.
		const resetAsked = resyncRequests !== resyncsHandled.current;
		resyncsHandled.current = resyncRequests;
		if (!resetAsked) {
			if (bandsKey(serverBands) === bandsKey(lastCommitted)) {
				return;
			}
			// The response to a write of our own, arriving before the write was recorded. Nothing to adopt:
			// this is what was just sent, and treating it as a change from elsewhere would throw away
			// anything done while it was in flight — adding a band straight after an edit was enough to see
			// the new row appear and then vanish.
			if (inFlight.current && bandsKey(serverBands) === bandsKey(inFlight.current)) {
				return;
			}
		}
		setBands(serverBands);
		setErrors([]);
		setEndDrafts({});
		setWinProbabilityDrafts({});
		recordCommitted(serverBands);
	}, [serverBands, lastCommitted, resyncRequests]);

	// Child effects run before the parent's, so the row has taken the cursor by the time this clears the
	// request that told it to.
	useEffect(() => {
		if (focusIndex !== null) {
			setFocusIndex(null);
		}
	}, [focusIndex]);

	/**
	 * Shows a reason the server stated, and hands anything else to the standard handling.
	 *
	 * Without this the mapped reason is thrown away: the default path shows the generic mutation modal,
	 * which has no message field, and it recovers a correlation id only from its own known prefixes — so
	 * an unrecognised message loses the id too, leaving the user with an opaque modal and support with
	 * nothing to search for.
	 */
	const handleMutationError = (transaction: unknown, onFailure: (transaction: unknown) => void) => {
		const parsed = parseServerRejection(String(transaction));
		if (!parsed) {
			onFailure(transaction);
			return;
		}
		setRejection(parsed);
	};

	const sendReset = () => {
		resetInFlight.current = true;
		// Whatever was held is part of the set being discarded, so it does not outlive the reset.
		pending.current = null;

		Util.CommitMutation(
			ResetOpportunityStageBandsMutation,
			{},
			() => {
				resetInFlight.current = false;
				setRejection(null);
				// Reset means the page goes back to what the server holds, whatever was on screen. The
				// half-finished edits it discards are the reason it was reached for, so they are dropped
				// here rather than left to the resync effect: that effect declines to overwrite local
				// state when the server's set matches what was last written, which is exactly the case
				// for an account already on the defaults — leaving a cleared field and its error sitting
				// on a page that has just taken the defaults it is complaining about.
				setErrors([]);
				setEndDrafts({});
				setWinProbabilityDrafts({});
				// Asking for the resync outright, so the set itself comes from the server's response rather
				// than from anything assumed here — and without emptying what was last committed, which made
				// every row look unsaved until the resync ran.
				setResyncRequests(requests => requests + 1);
			},
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => void) => {
				resetInFlight.current = false;
				handleMutationError(transaction, onFailure);
			}
		);
	};

	/**
	 * Closes off a write whose result no longer applies, and releases a reset that was waiting behind it.
	 *
	 * The response is the only evidence available that the server has finished with that write, so it is
	 * what a reset has to wait for: sent before it, the two commit in whichever order the server finishes
	 * them, and a write finishing second restores the set the reset discarded.
	 */
	const settleSupersededWrite = () => {
		inFlight.current = null;
		if (pendingReset.current) {
			pendingReset.current = false;
			sendReset();
		}
	};

	/**
	 * Writes one set, and on completion writes whatever arrived while it was out.
	 *
	 * Only ever called with nothing already in flight. Sending unconditionally is what the page used to do,
	 * and it is why several writes could be out at once: the responses then returned in whatever order the
	 * network chose, the server kept whichever landed last rather than whichever was sent last, and the page
	 * — which can only recognise one write of its own — took the others for another admin's change and
	 * adopted them over whatever had been typed since.
	 */
	const sendBands = (set: StageBand[]) => {
		inFlight.current = set;
		const generation = writeGeneration.current;

		Util.CommitMutation(
			UpdateOpportunityStageBandsMutation,
			{
				// Each band's id goes back so the server updates that row in place instead of replacing the
				// set; a band the admin just added has none, and the server inserts it.
				bands: set.map(band => ({
					id: band.id,
					name: band.name.trim(),
					endPercentage: band.end,
					winProbability: band.winProbability,
				})),
			},
			() => {
				if (generation !== writeGeneration.current) {
					// Superseded while it was out, so its result describes a set the page has moved on from.
					// The write is finished at the server though, which is the signal a waiting reset needs.
					settleSupersededWrite();
					return;
				}
				inFlight.current = null;
				setRejection(null);
				recordCommitted(set);

				const queued = pending.current;
				pending.current = null;
				// Anything held while this was out goes now. Compared against what just landed because a
				// blur can queue a set identical to the one being written — tabbing back through a row that
				// was already saved — and posting it again would spend a round trip changing nothing.
				if (queued && bandsKey(queued) !== bandsKey(set)) {
					sendBands(queued);
					return;
				}
				// Confirmed only once the queue is empty, so a burst of edits is confirmed when the last of
				// them reaches the server rather than once per write. Confirming each write instead is what
				// produced a run of toasts still arriving after the admin had stopped typing. The page-local
				// copy (naming saved filters) replaces the bare "Changes saved" every other settings page
				// shows; the data-cy makes the confirmation addressable.
				createToast({
					duration: 2000,
					message: (
						<span data-cy="opportunity-stages-save-status">
							{formatMessage({id: 'settings.opportunity_stages.changes_saved'})}
						</span>
					),
				});
			},
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => void) => {
				if (generation !== writeGeneration.current) {
					// Refused or dropped, and superseded either way. Not handed to the standard handling: that
					// would retry a set the page has already abandoned. The request is over, so a waiting
					// reset can go — whether the server applied this write or not, the reset now lands after.
					settleSupersededWrite();
					return;
				}
				const parsed = parseServerRejection(String(transaction));
				if (!parsed) {
					// Not a reason the server stated, so the standard handling decides what it is — and for a
					// transient network error that means scheduling a retry, which it reports by returning
					// true. That retry re-sends this same set, so the write is still outstanding: clearing
					// the marker here would let the next blur send a second write alongside it, and the
					// retry's older payload could then land last and overwrite the newer one.
					const retryScheduled = (onFailure as (t: unknown) => unknown)(transaction) === true;
					if (retryScheduled) {
						return;
					}
					inFlight.current = null;
					pending.current = null;
					return;
				}
				// Refused with a reason.
				inFlight.current = null;
				setRejection(parsed);

				// A set held behind it still goes. It is usually a later copy of this same list, carrying
				// whatever was objected to, and will be refused for the same reason — one wasted request. But
				// it can also be the row being fixed, in which case sending it saves the correction now; and
				// it can carry an edit elsewhere that the refusal says nothing about, which dropping would
				// strand with only the earlier problem named on screen. A second refusal is the cheaper
				// mistake than an edit that never leaves the page.
				const heldAfterRefusal = pending.current;
				pending.current = null;
				if (heldAfterRefusal && bandsKey(heldAfterRefusal) !== bandsKey(set)) {
					sendBands(heldAfterRefusal);
				}
			}
		);
	};

	const persist = (next: StageBand[]) => {
		const found = validateBands(next);
		setErrors(found);
		// The whole list is posted together, so one invalid row blocks the entire commit. The entered
		// value is deliberately kept rather than reverted — reverting would discard what the user was
		// part-way through expressing.
		if (found.length > 0) {
			return;
		}
		// A reset decides the whole set, so nothing is sent or held while one is out or waiting to go.
		if (resetInFlight.current || pendingReset.current) {
			return;
		}
		// What is outstanding has to be judged before what is committed, because while a write is out the
		// committed set is the one from *before* it. Comparing against that first treats "the same as it was
		// two edits ago" as "nothing to do" — so undoing an edit while it is still saving would be dropped,
		// leaving the server holding the value and the screen showing the one it was changed back from.
		const outstanding = pending.current ?? inFlight.current;
		if (outstanding) {
			// Held rather than sent: one write at a time is what lets a returning response be recognised as
			// this page's own. Only if it says something new — re-blurring a row already being written, or
			// already held, asks for nothing.
			if (bandsKey(next) !== bandsKey(outstanding)) {
				pending.current = next;
			}
			return;
		}
		if (bandsKey(next) === bandsKey(lastCommittedRef.current)) {
			// Nothing to send, and nothing outstanding, so the page matches the server exactly. Any earlier
			// failure was about a set that no longer exists — leaving it reported would describe a problem
			// the user has already resolved by backing the edit out, with no write left that could clear it.
			setRejection(null);
			return;
		}

		sendBands(next);
	};

	// Dropping the drafts on blur is what normalises the display: a draft is the text exactly as typed, so
	// "007" would otherwise stay on screen against a band holding 7 until the next server read.
	const commitCurrent = () => {
		setEndDrafts({});
		setWinProbabilityDrafts({});
		persist(bands);
	};

	const changeName = (index: number, name: string) =>
		setBands(current => current.map((band, bandIndex) => (bandIndex === index ? {...band, name} : band)));

	// The typed text is kept alongside the parsed number so clearing the field shows an empty box rather
	// than "NaN"; the parsed value stays NaN, which validateBands already rejects, so the commit is blocked.
	const changeEnd = (index: number, raw: string) => {
		const digitsOnly = raw.replace(/[^0-9]/g, '');
		setEndDrafts(current => ({...current, [index]: digitsOnly}));
		const parsed = Number.parseInt(digitsOnly, 10);
		setBands(current => setBandEnd(current, index, Number.isNaN(parsed) ? NaN : parsed));
	};

	const endTextFor = (index: number, end: number): string => textFor(endDrafts, index, end);

	const winProbabilityTextFor = (index: number, winProbability: number): string =>
		textFor(winProbabilityDrafts, index, winProbability);

	const changeWinProbability = (index: number, raw: string) => {
		const digitsOnly = raw.replace(/[^0-9]/g, '');
		setWinProbabilityDrafts(current => ({...current, [index]: digitsOnly}));
		const parsed = Number.parseInt(digitsOnly, 10);
		setBands(current => setBandWinProbability(current, index, Number.isNaN(parsed) ? NaN : parsed));
	};

	// Delete is a discrete action with no half-finished state, so it commits at once. Add cannot: the new
	// band starts with a blank label, which is invalid on its own. Rather than validating immediately —
	// which would flash an empty-name error on a field the user has not touched yet — the new row is
	// left unvalidated until its own blur triggers a commit, the same as any other edit.
	// That blur is why the appended row takes the cursor: the click that adds it leaves focus on the Add
	// button, so a user who then clicks anywhere other than a field blurs nothing, and the empty label
	// goes unreported until they happen to touch some other row. Focusing the new row both puts the
	// cursor where they are about to type and makes leaving it an ordinary blur, which reports the error.
	// Both operations renumber rows, and drafts are held per row index, so any surviving draft would be
	// shown against a different band than the one it was typed into — a half-typed "15" appearing on the
	// row that inherited that index, with the preview and the next commit using the real value instead.
	const handleAdd = () => {
		const next = addBand(bands, '');
		// Nothing was appended — the set is full or the last band is mid-edit — so there is no row to focus.
		if (next.length === bands.length) {
			return;
		}
		setBands(next);
		setEndDrafts({});
		setWinProbabilityDrafts({});
		setFocusIndex(next.length - 1);
	};

	const handleDelete = (index: number) => {
		const next = deleteBand(bands, index);
		setBands(next);
		setEndDrafts({});
		setWinProbabilityDrafts({});
		persist(next);
	};

	const handleReset = () =>
		showResetOpportunityStagesModal({
			onReset: () => {
				// Anything merely queued is obsolete and goes now: it would post the very edits the reset was
				// asked to discard.
				writeGeneration.current += 1;
				pending.current = null;

				if (inFlight.current) {
					// A write is already at the server and cannot be recalled, so the reset waits for its
					// response rather than racing it.
					pendingReset.current = true;
					return;
				}

				sendReset();
			},
		});

	const bandsWithStarts = withStarts(bands);

	const addStageButton = (
		<Button
			emphasis={EMPHASIS.LOW}
			icon={'plus'}
			disabled={!canAddBand(bands)}
			onPress={handleAdd}
			data-cy={'opportunity-stages-add'}
		>
			{formatMessage({id: 'settings.opportunity_stages.add'})}
		</Button>
	);
	// The cap is a product rule, so Add really is unavailable — but a control that simply stops responding
	// reads as a broken button, especially with the blank row's own error already on screen suggesting the
	// two are related, so the limit is stated on hover. `interactable={false}` is what makes that reachable:
	// the button carries a real `disabled` attribute and so emits no pointer events of its own, and without
	// it the tooltip would be attached to a trigger that can never fire. Given false, the tooltip wraps the
	// button in a trigger element of its own and hovers that instead.
	const addStageControl =
		addBandBlock(bands) === 'cap' ? (
			<Tooltip
				content={formatMessage({id: 'settings.opportunity_stages.max_reached'}, {max: MAX_BANDS})}
				interactable={false}
			>
				{addStageButton}
			</Tooltip>
		) : (
			addStageButton
		);

	return (
		<div className="section-content settings-app" data-cy={'settings-opportunity-stages-page'}>
			<ForecastThemeProvider>
				<>
					<TopHeaderBarWrapper>
						<TopHeaderBar title={formatMessage({id: 'settings.opportunity_stages.title'})} />
					</TopHeaderBarWrapper>
					<CustomScrollDiv>
						<Page>
							<Subtitle data-cy={'opportunity-stages-subtitle'}>
								<Text color={'medium'}>{formatMessage({id: 'settings.opportunity_stages.subtitle'})}</Text>
							</Subtitle>
							{/* Full-width dismissible information banner, matching the Currencies settings page. Both
							    paragraphs go through the alert's `text`, which renders them as its own children —
							    its `children` slot would put the second one beside the first rather than below it. */}
							{!infoDismissed && (
								<AlertContainer data-cy={'opportunity-stages-info'}>
									<Alert
										type={Alert.TYPE.INFORMATION}
										text={asAlertText(
											<>
												<BannerParagraph>
													{formatMessage(
														{id: 'settings.opportunity_stages.description'},
														{b: (chunks: React.ReactNode) => <b>{chunks}</b>}
													)}
												</BannerParagraph>
												<BannerParagraph>
													{formatMessage({id: 'settings.opportunity_stages.description_detail'})}
												</BannerParagraph>
											</>
										)}
										onDismiss={() => setInfoDismissed(true)}
									/>
								</AlertContainer>
							)}

							<HeadingContainer>
								<FlexColumn gap={'l'}>
									<Heading size={4}>
										{formatMessage({id: 'settings.opportunity_stages.preview_title'})}
									</Heading>
									<Separator />
								</FlexColumn>
							</HeadingContainer>

							{/* Read-only preview: equal-width segments with nothing selected and no change handler. */}
							<PreviewBar>
								<StageBandBar
									value={''}
									dataCy={'opportunity-stages-preview'}
									items={bandsWithStarts.map((band, index) => ({
										value: String(index),
										// The bar answers "what will a seller land on", which is the single value,
										// not the band. The range stays fully visible one row below.
										topText: formatWinProbability(band.winProbability),
										// An unnamed band still has to occupy its segment, or a row added by Add
										// would collapse out of the preview until it was given a label.
										label: band.name || '—',
										dataCy: `opportunity-stages-preview-band-${index}`,
									}))}
								/>
							</PreviewBar>

							{/* Persistent, not dismissible: it describes a consequence of the edits made below,
							    so it has to be visible at the moment the admin is making them. Rendered as an
							    information alert to match the page's other info banner — omitting onDismiss keeps
							    it from being dismissable. */}
							<AlertContainer data-cy={'opportunity-stages-filter-warning'}>
								<Alert
									type={Alert.TYPE.INFORMATION}
									text={formatMessage({id: 'settings.opportunity_stages.filter_warning'})}
								/>
							</AlertContainer>

							{/* A save the server refused, with the reason it gave. The correlation id rides along so
							    support can find the request — the generic modal path loses both. */}
							{rejection && (
								<AlertContainer data-cy={'opportunity-stages-save-error'}>
									<Alert
										type={Alert.TYPE.WARNING}
										text={
											rejection.correlationId
												? `${rejection.reason} (${rejection.correlationId})`
												: rejection.reason
										}
										onDismiss={() => setRejection(null)}
									/>
								</AlertContainer>
							)}

							<FlexColumn>
								<HeaderRow>
									<Label>{formatMessage({id: 'settings.opportunity_stages.column_stage'})}</Label>
									<Label>{formatMessage({id: 'settings.opportunity_stages.column_win_probability'})}</Label>
									<Label>{formatMessage({id: 'settings.opportunity_stages.column_range'})}</Label>
									<div />
								</HeaderRow>

								<LockedOpportunityStageRow
									label={formatMessage({id: 'opportunities.stage.lost'})}
									percentage={FIXED_LOST_PERCENTAGE}
									dataCy={'opportunity-stage-row-lost'}
								/>

								{bandsWithStarts.map((band, index) => (
									<EditableOpportunityStageRow
										key={index}
										index={index}
										name={band.name}
										start={band.start}
										endText={endTextFor(index, band.end)}
										isLast={index === bandsWithStarts.length - 1}
										winProbabilityText={winProbabilityTextFor(index, band.winProbability)}
										nameError={errorTextFor(errors, index, 'name', formatMessage)}
										endError={errorTextFor(errors, index, 'end', formatMessage)}
										winProbabilityError={errorTextFor(errors, index, 'winProbability', formatMessage)}
										autoFocus={index === focusIndex}
										canDelete={canDeleteBand(bands)}
										onNameChange={value => changeName(index, value)}
										onEndChange={value => changeEnd(index, value)}
										onWinProbabilityChange={value => changeWinProbability(index, value)}
										onCommit={commitCurrent}
										onDelete={() => handleDelete(index)}
									/>
								))}

								<LockedOpportunityStageRow
									label={formatMessage({id: 'opportunities.stage.won'})}
									percentage={FIXED_WON_PERCENTAGE}
									dataCy={'opportunity-stage-row-won'}
								/>
								<Actions>
									<FlexRow justifyContent={'space-between'} alignItems={'center'}>
										{addStageControl}
										<Button
											emphasis={EMPHASIS.LOW}
											icon={'reload'}
											onPress={handleReset}
											data-cy={'opportunity-stages-reset'}
										>
											{formatMessage({id: 'settings.opportunity_stages.reset'})}
										</Button>
									</FlexRow>
								</Actions>
							</FlexColumn>
						</Page>
					</CustomScrollDiv>
				</>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsOpportunityStagesQuery = graphql`
	query SettingsOpportunityStagesQuery {
		viewer {
			actualPersonId
			component(name: "settings_opportunity_stages")
			...SettingsOpportunityStages_viewer
		}
	}
`;

export default SettingsOpportunityStages;
